import { bind } from 'decko'
import { LiveElement, LiveNodeList } from 'live-node-list'
import {
  Product as ShopifyProduct,
  ProductForm as ShopifyProductForm,
} from '@shopify/theme-product-form'
import ProductDataCache from './product-data-cache'
import {
  ProductOptionChangeEvent,
  ProductPropertyChangeEvent,
  ProductQuantityChangeEvent,
} from './product-events'
import ProductForm from './product-form'
import { goToSlide } from './slideshow-helpers'

export default class Product {
  /**
   * @type {Map<string, ShopifyProduct>}
   */
  static cache = new ProductDataCache()

  /**
   * @type {LiveNodeList<HTMLFormElement>}
   */
  productForms = new LiveNodeList('form[action*="/cart/add"]')

  /**
   * @type {LiveElement<HTMLElement>}
   */
  productBuyBar = new LiveElement('.product-buy-bar')

  /**
   * @type {LiveElement<HTMLButtonElement>}
   */
  productDropRodModalTrigger = new LiveElement(
    '.product-options__drop-rod-modal-trigger',
  )

  /**
   * @type {LiveElement<HTMLElement>}
   */
  productDropRodModals = new LiveNodeList(
    '.product-options__drop-rod-length-modal, .product-options__drop-rod-length-help',
  )

  /**
   * @type {LiveElement<HTMLButtonElement>}
   */
  productDropRodModalClose = new LiveElement(
    '.product-options__drop-rod-length-modal-close',
  )

  /**
   * @type {LiveNodeList<HTMLButtonElement>}
   */
  productImages = new LiveNodeList('.product__image-grid-item')

  /**
   * @type {LiveElement<HTMLDivElement>}
   */
  productSlideshowModal = new LiveElement('.product__slideshow-container')

  /**
   * @type {LiveElement<HTMLUListElement>}
   */
  productSlideshow = new LiveElement('#product-slideshow')

  /**
   * @type {LiveElement<HTMLButtonElement>}
   */
  productSlideshowClose = new LiveElement('.product-slideshow__close')

  constructor() {
    this.productForms.items.forEach(this.init)
    this.registerListeners()
  }

  registerListeners() {
    this.productForms.on('update', newItems => {
      newItems.forEach(this.init)
    })

    this.productBuyBar.addDelegatedEventListener(window, 'scroll', () => {
      const form = this.productBuyBar.item.closest('.product-content-form')
      const { top, height } = form.getBoundingClientRect()

      this.productBuyBar.item.setAttribute(
        'aria-hidden',
        (top + height > 0).toString(),
      )
    })

    this.productDropRodModalTrigger.addEventListener('click', event => {
      event.preventDefault()
      const hidden =
        this.productDropRodModals.items[0]?.getAttribute('aria-hidden') ===
        'true'
      this.productDropRodModalTrigger.item?.setAttribute(
        'aria-expanded',
        hidden ? 'true' : 'false',
      )
      this.productDropRodModals.forEach(item =>
        item?.setAttribute('aria-hidden', hidden ? 'false' : 'true'),
      )
    })

    this.productDropRodModalClose.addEventListener('click', event => {
      event.preventDefault()
      this.productDropRodModals.forEach(item =>
        item?.setAttribute('aria-hidden', 'true'),
      )
    })

    this.productImages.addEventListener('click', event => {
      const image = event.currentTarget || event.target
      this.productSlideshowModal.item?.setAttribute('aria-hidden', 'false')

      setTimeout(() => {
        goToSlide(this.productSlideshow.item, image.dataset.index)
      }, 5)
    })


    this.productSlideshowClose.addEventListener('click', event => {
      this.productSlideshowModal.item?.setAttribute('aria-hidden', 'true')
    })
  }

  @bind
  async init(form) {
    const handle = form.dataset.productHandle
    const product = await this.constructor.cache.get(handle)
    const variant = product.variants.find(
      variant => parseInt(variant.id) === parseInt(form.dataset.variantId),
    )

    form.shopifyProductForm = new ShopifyProductForm(form, product, {
      onOptionChange: event => {
        form.dispatchEvent(
          new ProductOptionChangeEvent({
            form: form,
            options: event.dataset.options,
            product: product,
            properties: event.dataset.properties,
            quantity: event.dataset.quantity,
            variant: event.dataset.variant,
            parentEvent: event,
          }),
        )
      },

      onQuantityChange: event => {
        form.dispatchEvent(
          new ProductQuantityChangeEvent({
            form: form,
            options: event.dataset.options,
            product: product,
            properties: event.dataset.properties,
            quantity: event.dataset.quantity,
            variant: event.dataset.variant,
            parentEvent: event,
          }),
        )
      },

      onPropertyChange: event => {
        form.dispatchEvent(
          new ProductPropertyChangeEvent({
            form: form,
            options: event.dataset.options,
            product: product,
            properties: event.dataset.properties,
            quantity: event.dataset.quantity,
            variant: event.dataset.variant,
            parentEvent: event,
          }),
        )
      },
    })

    form.productForm = new ProductForm(form, product, variant)
  }
}
